'use client'

import { FC } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import Title from '@/app/components/common/Title'
import Text from '@/app/components/common/Text'
import { BlogPost } from '@/app/types/blog'
import { getSanityImageUrl } from '@/app/api/sanity'
import { Icons } from '@/app/components/ui/Icons'
import ArticleCategoryChip from './ArticleCategoryChip'
import '@/app/styles/components/ArticleHeader.css'
import '@/app/styles/components/ArticleCategoryChip.css'
import { format } from 'date-fns'

interface Props {
	post: BlogPost
	lang: string
}

const ArticleHeader: FC<Props> = ({ post, lang }) => {
	const t = useTranslations('Blog')
	const imageUrl = getSanityImageUrl(post.mainImage, { width: 1200, quality: 90 })

	// Format date: "August 19, 2025"
	const formattedDate = post.publishedAt
		? format(new Date(post.publishedAt), 'MMMM d, yyyy')
		: ''

	const { categories } = post

	return (
		<header className="article-header">
			{imageUrl && (
				<div className="article-header__image-wrapper">
					<Image
						src={imageUrl}
						alt={post.mainImage?.alt || post.title}
						width={1200}
						height={675}
						priority
						className="article-header__image"
						sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 1200px"
					/>
				</div>
			)}

			<div className="article-header__content">
				<Link href={`/${lang}/blog`} className="article-header__back">
					<Icons.iconArrowLeft
						aria-hidden
						className="article-header__back-icon"
						stroke="currentColor"
						width="16"
						height="16"
					/>
					<span>{t('allArticlesTitle')}</span>
				</Link>

				<Title level={1} bold color="text-900" className="article-header__title" testId="article-header-title">
					{post.title}
				</Title>

				{post.excerpt && (
					<Text tag="p" color="text-800" className="article-header__excerpt">
						{post.excerpt}
					</Text>
				)}

				<div className="article-header__meta">
					{categories.length > 0 && (
						<div className="article-header__categories">
							{categories.map((category) => (
								<ArticleCategoryChip
									key={category._id}
									href={`/${lang}/blog/topics/${category.slug.current}?from=article`}
								>
									{category.title}
								</ArticleCategoryChip>
							))}
						</div>
					)}

					<div className="article-header__info">
						<span className="article-header__author">
							{t('byAuthor', { author: post.author })}
						</span>
						<span className="article-header__separator">·</span>
						<span className="article-header__date">{formattedDate}</span>
						<span className="article-header__separator">·</span>
						<span className="article-header__reading-time">
							{t('readingTime', { minutes: post.readingTime })}
						</span>
					</div>
				</div>
			</div>
		</header>
	)
}

export default ArticleHeader
